import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { MatomoTracker } from 'ngx-matomo-client';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { GuestService } from 'src/services/guest-service';
import { ActiveGymFacade } from 'src/store/active-gym/active-gym.facade';
import { NewAscent } from '../../components/add-ascent/AddAscentComponent';
import { BoulderFilterComponent } from '../../components/boulder-filter/boulder-filter.component';
import { PointsReceivedComponent } from '../../components/points-received/PointsReceivedComponent';
import { BoulderFilter } from '../../model/boulder-filter';
import { BoulderListEntry } from '../../model/boulder-list-entry';
import { GymListEntry } from '../../model/gym-list-entry';
import { SortBoulderListPipe } from '../../pipes/sort-boulder-list.pipe';
import { AuthService } from '../../services/auth.service';
import { BoulderService } from '../../services/boulder.service';
import { FeedItemService } from '../../services/feed-item.service';
import { GymService } from '../../services/gym.service';
import { RankingService } from '../../services/ranking.service';
import { BBStorage } from '../../utils/bb-storage';
import { BoulderDetailsPage } from '../boulder-details/boulder-details.page';

const INFINITE_SCROLL_STEP = 20;

@Component({
  selector: 'app-boulder-list',
  templateUrl: './boulder-list.page.html',
  styleUrls: ['./boulder-list.page.scss']
})
export class BoulderListPage implements OnInit, OnDestroy {

  activeGymIsBeingDownloaded = signal(false);
  activeGymIsBeingResynced = signal(false);

  // true if gym is being synced and boulders being loaded
  isBeingRefreshed = false;

  searchMode = false;
  searchString = '';

  // all boulders
  allBoulders: BoulderListEntry[] = [];

  // all boulders for gym that pass boulderfilter
  allFilteredBoulders: BoulderListEntry[] = [];

  // only visible boulders (pagination)
  visibleBoulders: BoulderListEntry[] = [];

  activeGym: GymListEntry;
  activeSegment = 'boulders';

  // gym name and bb-logo press state
  gymNameBeingPressed = false;
  logoBeingPressed = false;

  private ngUnsubscribe = new Subject<void>();

  constructor(
    public guestService: GuestService,
    public authService: AuthService,
    private matomoTracker: MatomoTracker,
    private logger: NGXLogger,
    private activatedRoute: ActivatedRoute,
    public gymService: GymService,
    private feedItemService: FeedItemService,
    private rankingService: RankingService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    public bbStorage: BBStorage,
    private translate: TranslateService,
    private boulderService: BoulderService,
    private activeGymFacade: ActiveGymFacade,
  ) {
    this.activeGymFacade.activeGymIsBeingDownloaded$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(isBeingDownloaded => {
      console.log('activeGymIsBeingDownloaded', isBeingDownloaded);
      this.activeGymIsBeingDownloaded.set(isBeingDownloaded);
    });
    this.activeGymFacade.activeGymsIsBeingResynced$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(isBeingResynced => {
      console.log('activeGymIsBeingResynced', isBeingResynced);
      this.activeGymIsBeingResynced.set(isBeingResynced);
    });
    this.activeGymFacade.activeGym$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((gym) => {
      this.logger.debug('activeGym changed', gym);
      if (this.activeGym?.gymId !== gym?.gymId) {
        this.activeGym = gym;
        this.refresh();
      } else if (!gym) {
        this.activeGym = null;
      }
    });
    this.activeGymFacade.onActiveGymFinishedDownloading$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.refresh();
    });
    this.activeGymFacade.onActiveGymFinishedResyncing$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.refresh();
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnInit() {
    this.listenToRouteParams();
    this.matomoTracker.trackPageView('boulder-list');
  }

  async showBoulder(boulderId: string) {
    const modal = await this.modalCtrl.create({
      id: 'boulder-details-modal',
      component: BoulderDetailsPage,
      componentProps: {
        boulder: this.allFilteredBoulders.filter(b => b.boulderId === boulderId)[0],
        boulderId,
        boulderIds: this.visibleBoulders.map(b => b.boulderId)
      }
    });
    modal.onDidDismiss().then(async (detail) => {
      if (detail?.data?.wasTicked) {
        const boulder = this.visibleBoulders.filter(b => b.boulderId === boulderId)[0];
        if (boulder) {
          // dirty way of immediately updating the entry before waiting for refresh
          (boulder as any).ticked = true;
          (boulder as any).numAscent = boulder.numAscents + 1;
        }
        if (detail?.data?.newAscent) {
          this.showModalIfPointsReceivedInRanking(boulderId, detail.data.newAscent);
        }
      }
      if (detail?.data?.isDirty) {
        this.refresh();
      }
    });
    return await modal.present();
  }

  async onRefresh(event) {
    try {
      this.isBeingRefreshed = true;
      await this.gymService.syncActiveGym();
      await this.loadBouldersAndUpdateActiveGym();
    } finally {
      if (event) {
        event.target.complete();
      }
    }
  }

  private async loadBouldersAndUpdateActiveGym() {
    const start = performance.now();
    this.isBeingRefreshed = true;
    await this.loadBoulders();
    // do async
    this.feedItemService.fetchNewDocsAndUpdateFeedItems();
    this.isBeingRefreshed = false;
    this.logger.debug('loadBouldersAndUpdateActiveGym took ', performance.now() - start);
  }

  async refresh() {
    this.logger.debug('refreshing boulder list');
    await this.onRefresh(null);
    this.gymNameBeingPressed = false;
    this.logoBeingPressed = false;
  }

  private async loadBoulders() {
    const start = performance.now();
    this.allBoulders = await firstValueFrom(this.boulderService.getBouldersListEntries$());
    this.allFilteredBoulders = this.allBoulders.filter(b => BoulderFilter.passesFilter(b, this.bbStorage.boulderFilter));
    this.sortAllBouldersAndShowTopList();
    this.logger.debug(`loadBoulders took ${performance.now() - start}`);
  }

  listBoulders(event) {
    setTimeout(() => {
      event.target.complete();
      this.visibleBoulders = this.visibleBoulders.concat(
        this.allFilteredBoulders.slice(this.visibleBoulders.length, this.visibleBoulders.length + INFINITE_SCROLL_STEP));
    }, 500);
  }

  private sortAllBouldersAndShowTopList() {
    SortBoulderListPipe.sortBoulderList(this.allFilteredBoulders, this.bbStorage.boulderListSortType);
    this.visibleBoulders = this.allFilteredBoulders.slice(0, INFINITE_SCROLL_STEP);
  }

  async filterIconClicked() {
    const modal = await this.modalCtrl.create({
      component: BoulderFilterComponent
    });
    modal.onDidDismiss().then((detail) => {
      if (detail?.data?.changed) {
        this.loadBoulders();
      }
    });
    return await modal.present();
  }

  filterIsActive() {
    return !BoulderFilter.isEmpty(this.bbStorage.boulderFilter);
  }


  private async askToSyncAndActivateGym(gymId: any) {
    return new Promise<boolean>(async (resolve, reject) => {
      const alert = await this.alertCtrl.create({
        header: this.translate.instant('boulder-list-page.change-gym-header'),
        message: this.translate.instant('boulder-list-page.change-gym-to-show-boulder'),
        buttons: [{
          text: this.translate.instant('boulder-list-page.yes'),
          handler: async () => {
            const gym = await firstValueFrom(this.gymService.getGymListEntry$(gymId));
            this.activeGymFacade.activeGymChanged(gym);
            resolve(true);
          }
        },
        {
          text: this.translate.instant('boulder-list-page.no'),
          role: 'cancel',
          handler: () => reject()
        }],
      });
      await alert.present();
      await alert.onDidDismiss();
    });
  }

  private async goToBoulder(gymId: any, boulderId: any) {
    const activeGymId = await firstValueFrom(this.activeGymFacade.activeGymId$);
    if (gymId === activeGymId || await this.askToSyncAndActivateGym(gymId)) {
      await this.showBoulder(boulderId);
    }
  }

  gymOrLogoPressReleased() {
    this.gymNameBeingPressed = false;
    this.logoBeingPressed = false;
  }

  gymNamePressed() {
    this.gymNameBeingPressed = true;
    this.logoBeingPressed = false;
    setTimeout(async () => {
      if (this.gymNameBeingPressed) {
        const alert = await this.alertCtrl.create({
          message: this.translate.instant('boulder-list-page.switch-to-fixed-gym-mode'),
          buttons: [{
            text: this.translate.instant('boulder-list-page.yes'),
            handler: () => {
              this.bbStorage.fixedGymMode = true;
            }
          },
          {
            text: this.translate.instant('boulder-list-page.no'),
            role: 'cancel'
          }],
        });
        await alert.present();
      }
    }, 20000);
  }

  logoPressed() {
    this.gymNameBeingPressed = false;
    this.logoBeingPressed = true;
    setTimeout(() => {
      if (this.logoBeingPressed) {
        this.bbStorage.fixedGymMode = false;
      }
    }, 10000);
  }

  private listenToRouteParams() {
    this.activatedRoute.params.subscribe(async params => {
      if (params['gymId'] && params['boulderId']) {
        await this.goToBoulder(params['gymId'], params['boulderId']);
      }
    });
    this.activatedRoute.queryParams.subscribe(async params => {
      if (params['reload']) {
        this.refresh();
      }
    });
  }

  noGymChosen(): boolean {
    return !this.activeGym && !this.isBeingRefreshed
  }

  isLoadingActiveGym() {
    return !this.activeGym && this.isBeingRefreshed;
  }

  isInitiallyLoadingBoulders() {
    return this.activeGym && (this.visibleBoulders === null || (this.visibleBoulders?.length === 0 && this.isBeingRefreshed));
  }

  noFilteredBoulders() {
    return this.allFilteredBoulders?.length === 0
  }

  noBoulders() {
    return this.allBoulders && this.allBoulders.length === 0;
  }

  showNoBouldersForCurrentFilter() {
    return !this.isBeingRefreshed && this.filterIsActive();
  }

  private async showModalIfPointsReceivedInRanking(boulderId: string, newAscent: NewAscent) {
    const pointsReceived: number = await this.rankingService.rankingPointsReceivedForSend(this.authService.getCurrentUsername(), boulderId, newAscent);
    if (pointsReceived > 0) {
      await this.showPointsReceivedAnimation(pointsReceived);
    }
  }

  private async showPointsReceivedAnimation(pointsReceived: number) {
    const modal = await this.modalCtrl.create({
      component: PointsReceivedComponent,
      componentProps: { pointsReceived: pointsReceived }
    });
    await modal.present();
  }
}
